import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaShieldAlt } from 'react-icons/fa';
import Header from '../components/Header';
import talent14Logo from '../assets/TALENT14_LOGO.png';
import style from './Kvkk.module.css';

const kvkkDocs = [
  {
    title: 'Çerez ve Gizlilik Politikası',
    desc: 'Web sitemizde kullanılan çerezler ve kişisel verilerinizin nasıl korunduğuna dair politikamız.',
    href: 'https://bilisimschool.com/kvkk/cerez_ve_gizlilik_politikasi.pdf',
  },
  {
    title: 'Santral Hizmetleri Aydınlatma Metni',
    desc: 'Santral hizmetleri kapsamında kişisel verilerinizin işlenmesine ilişkin aydınlatma metni.',
    href: 'https://bilisimschool.com/kvkk/santral_hizmetleri_aydinlatma_metni.pdf',
  },
  {
    title: 'Öğrenci ve Veli Aydınlatma Metni',
    desc: 'Öğrenci ve veli kişisel verilerinin işlenmesi ve korunmasına dair aydınlatma metni.',
    href: 'https://bilisimschool.com/kvkk/ogrenci_ve_veli_aydınlatma_metni.pdf',
  },
  {
    title: 'Kişisel Veri Saklama ve İmha Politikası',
    desc: 'Kişisel verilerin ne kadar süreyle saklandığı ve nasıl imha edildiğine dair politikamız.',
    href: 'https://bilisimschool.com/kvkk/Kişisel_Veri_Saklama_ve_İmha_Politikası.pdf',
  },
  {
    title: 'İlgili Kişi Başvuru Formu ve Bilgilendirme Yazısı',
    desc: 'KVKK kapsamında haklarınızı kullanmak için başvuru formu ve bilgilendirme yazısı.',
    href: 'https://bilisimschool.com/kvkk/ilgili_kisi_basvuru_formu_ve_bilgilendirme_yazisi.pdf',
  },
  {
    title: 'Mesafeli Satış Sözleşmesi',
    desc: 'Uzaktan satış işlemlerine ilişkin yasal koşulları düzenleyen sözleşme metni.',
    href: 'https://bilisimschool.com/kvkk/mesafeli_satis_sozlesmesi.pdf',
  },
];

function DocIcon() {
  return (
    <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <rect width="44" height="44" rx="12" fill="rgba(108,93,211,0.12)" />
      <path
        d="M14 10h12l9 9v17a2 2 0 01-2 2H14a2 2 0 01-2-2V12a2 2 0 012-2z"
        stroke="#6c5dd3"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M26 10v9h9" stroke="#6c5dd3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M18 24h8M18 29h6" stroke="#6c5dd3" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 18 },
  visible: { opacity: 1, y: 0 },
};

const Kvkk = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    const previousTitle = document.title;
    document.title = 'Kişisel Verilerin Korunması (KVKK) — Talent14';
    return () => {
      document.title = previousTitle;
    };
  }, []);

  return (
    <div className={style.page}>
      <Header logo={talent14Logo} />

      <section className={style.hero}>
        <div className={style.heroGlow} />
        <div className={style.wrap}>
          <nav className={style.breadcrumb} aria-label="Sayfa konumu">
            <Link to="/" className={style.breadcrumbLink}>
              Ana Sayfa
            </Link>
            <span className={style.breadcrumbSep}>/</span>
            <span className={style.breadcrumbCurrent}>KVKK</span>
          </nav>

          <div className={style.heroCopy}>
            <p className={style.eyebrow}>
              <FaShieldAlt aria-hidden="true" />
              Gizlilik &amp; Güvenlik
            </p>
            <h1 className={style.title}>
              Kişisel Verilerin{' '}
              <span className={style.titleAccent}>Korunması (KVKK)</span>
            </h1>
            <p className={style.subtitle}>
              Kişisel verilerinizin güvenliği bizim için önceliktir. Aşağıdaki politika ve
              aydınlatma metinlerimizi inceleyebilir, PDF olarak görüntüleyebilirsiniz.
            </p>
          </div>
        </div>
      </section>

      <section className={style.docs}>
        <div className={style.wrap}>
          <motion.div
            className={style.grid}
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {kvkkDocs.map((doc) => (
              <motion.article key={doc.title} className={style.card} variants={itemVariants}>
                <div className={style.cardTop}>
                  <DocIcon />
                  <h2 className={style.cardTitle}>{doc.title}</h2>
                </div>
                <p className={style.cardDesc}>{doc.desc}</p>
                <a
                  href={doc.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={style.cardBtn}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path
                      d="M12 3v13M5 13l7 7 7-7"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  OKU / İNDİR
                </a>
              </motion.article>
            ))}
          </motion.div>
        </div>
      </section>

      <section className={style.contact}>
        <div className={style.wrap}>
          <p className={style.contactText}>
            Kişisel verilerinizle ilgili sorularınız veya başvurularınız için{' '}
            <a href="mailto:info@talent14.com" className={style.contactLink}>
              info@talent14.com
            </a>{' '}
            adresine ulaşabilirsiniz.
          </p>
        </div>
      </section>
    </div>
  );
};

export default Kvkk;
